import { IModalProps } from '@aon/aon-tws-common-ui';
import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../appStore';
import ContentConstants from '../../contentConstants';
import { SurveyStep } from '../../models';
import { ActionTypes as CommonActionTypes } from '../../store/ActionTypes';
import {
	getErrorCondition,
	loadQuestionBranchingLogic,
	resetQuestionBranchingLogic,
	saveQuestionBranchingLogic,
} from '../../store/QuestionBranchingLogicSlice';
import { ActionCreators as SurveyBuilderActionCreators } from '../../store/SurveyBuilderStore';
import { ActionCreators as SurveyActionCreators, selectSurveyQuestion } from '../../store/SurveyStore';
import { useModalState } from '../../utilities/hooks';
import { LoadingIndicator, WarningModal } from '../common';
import { DisplayLogicConditionErrorMessage } from '../displayLogic/DisplayLogicConditionErrorMessage';
import { ColumnLayoutModal } from './ColumnLayoutModal';
import { QuestionBranchingLogic } from './QuestionBranchingLogic';

type QuestionBranchingLogicModalProps = {
	questionId: number;
} & Pick<IModalProps, 'isOpen' | 'onClosed'>;

const QuestionBranchingLogicModal = ({ isOpen, questionId, onClosed }: QuestionBranchingLogicModalProps) => {
	const dispatch = useAppDispatch();

	const {
		surveyEditModel: {
			survey: { surveyId },
			audienceSource: { audienceSourceType },
		},
	} = useAppSelector((state) => state.survey);

	const { isOpen: isWarningModalOpen, toggleModal: toggleWarningModal } = useModalState();

	useEffect(() => {
		if (questionId > 0 && isOpen) {
			dispatch(loadQuestionBranchingLogic({ surveyId, questionId }));
		}
	}, [surveyId, questionId, isOpen, dispatch]);

	const question = useAppSelector((state) => selectSurveyQuestion(state, questionId));

	const { branchingLogicEditModels, isLoadingOrSaving, unsavedBranchingLogicChanges } = useAppSelector(
		(state) => state.questionBranchingLogic,
	);

	const reset = () => {
		dispatch(resetQuestionBranchingLogic());
	};

	const handleClosed = (sourceIsWarningModal?: boolean) => {
		if ((unsavedBranchingLogicChanges && !sourceIsWarningModal) || sourceIsWarningModal) {
			toggleWarningModal();
		}

		if (sourceIsWarningModal || (!sourceIsWarningModal && !unsavedBranchingLogicChanges)) {
			reset();
			onClosed();
		}
	};

	const handleSubmit = () =>
		dispatch(saveQuestionBranchingLogic({ surveyId, questionId }))
			.unwrap()
			.then(() => {
				reset();
				dispatch({ type: CommonActionTypes.TOAST_SUCCESS_GENERIC_NOTIFICATION });
				dispatch(SurveyActionCreators.UpdateSurveyQuestionDisplayRulesCount(questionId, branchingLogicEditModels.length, 'set'));
				onClosed();
			})
			.catch(() => {
				dispatch({
					type: CommonActionTypes.TOAST_WARNING_CUSTOM_NOTIFICATION,
					payload: {
						message: ContentConstants.TOAST_ERROR_GENERIC_BODY,
						messageTitle: ContentConstants.TOAST_ERROR_GENERIC_TITLE,
					},
				});
			});

	const jumpToStep = (destinationStep: SurveyStep) => {
		reset();
		onClosed();
		dispatch(SurveyBuilderActionCreators.Jump(destinationStep));
	};

	return (
		<ColumnLayoutModal
			contentfullwidth={true}
			contenthcentered={false}
			contentoverflow={true}
			isOpen={isOpen}
			onClosed={handleClosed}
			onSubmit={handleSubmit}
			primaryButtonText={ContentConstants.BUTTON_SAVE_CHANGES}
			secondaryButtonText={ContentConstants.BUTTONS_CLOSE}
			title={
				!branchingLogicEditModels.length
					? ContentConstants.DISPLAY_LOGIC_MODAL_CREATE_HEADER_TEXT
					: ContentConstants.DISPLAY_LOGIC_MODAL_EDIT_HEADER_TEXT
			}
		>
			<div className='row form-group'>
				<div className='col'>
					<DisplayLogicConditionErrorMessage
						ResetQuestionDisplayLogic={reset}
						Jump={jumpToStep}
						errorCondition={getErrorCondition(audienceSourceType)}
					/>
				</div>
			</div>
			<div className='row form-group'>
				<div className='col tws-question-text'>
					<b>{`"${question?.text}"`}</b>
				</div>
			</div>
			<div className='row'>
				<div className='col'>
					{isLoadingOrSaving ? <LoadingIndicator /> : <QuestionBranchingLogic questionId={questionId} scope='individual' />}
				</div>
			</div>
			<WarningModal
				contentBody={ContentConstants.WARNING_MODAL_UNSAVED_BODY}
				contentHeadline={ContentConstants.WARNING_MODAL_UNSAVED_HEADLINE}
				isOpen={isWarningModalOpen}
				onClose={toggleWarningModal}
				onSubmit={() => handleClosed(true)}
			/>
		</ColumnLayoutModal>
	);
};

export { QuestionBranchingLogicModal, type QuestionBranchingLogicModalProps };
